"use client"

import Image from "next/image"
import { useEffect, useRef, useState } from "react"

import type { CarouselApi } from "@/components/ui/carousel"
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel"
import { useToast } from "@/components/ui/use-toast"

const balloons = [
  { id: 1, left: "6%", size: 70, delay: "0s", duration: "18s", color: "bg-[#fde2e4]" },
  { id: 2, left: "16%", size: 80, delay: "2s", duration: "20s", color: "bg-[#f8bbd0]" },
  { id: 3, left: "28%", size: 65, delay: "4s", duration: "16s", color: "bg-[#ffe0f0]" },
  { id: 4, left: "40%", size: 90, delay: "1s", duration: "22s", color: "bg-[#fbcfe8]" },
  { id: 5, left: "55%", size: 75, delay: "3s", duration: "19s", color: "bg-[#fecdd3]" },
  { id: 6, left: "70%", size: 85, delay: "5s", duration: "21s", color: "bg-[#ffe4e6]" },
  { id: 7, left: "84%", size: 70, delay: "6s", duration: "17s", color: "bg-[#f9a8d4]" },
  { id: 8, left: "94%", size: 80, delay: "8s", duration: "23s", color: "bg-[#fde68a]" },
]

const confettiPieces = Array.from({ length: 35 }).map((_, index) => ({
  id: index,
  left: `${Math.random() * 100}%`,
  size: Math.random() * 8 + 6,
  delay: `${Math.random() * 6}s`,
  duration: `${12 + Math.random() * 10}s`,
  color: ["bg-red-400", "bg-pink-400", "bg-yellow-300", "bg-purple-300", "bg-sky-300"][index % 5],
}))

const PLACEHOLDER_IMAGE = "/placeholder-user.webp"

type BirthdayStudent = {
  id: string
  name: string
  standard: string
  image: string
}

export function BirthdaySection() {
  const [api, setApi] = useState<CarouselApi | null>(null)
  const pauseRef = useRef(false)
  const [students, setStudents] = useState<BirthdayStudent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    async function loadBirthdays() {
      try {
        // Use local API for development, production API for deployment
        const apiUrl = process.env.NEXT_PUBLIC_BIRTHDAY_API_URL ||
          (typeof window !== 'undefined' && window.location.hostname === 'localhost'
            ? "/api/birthdays"
            : "https://us-central1-minicreators.cloudfunctions.net/api/birthdays");
        const response = await fetch(apiUrl, {
          cache: "no-store",
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }

        const data = (await response.json()) as { students?: BirthdayStudent[], success?: boolean, error?: string, details?: string }
        if (!isMounted) return

        if (data.success === false) {
          console.error("❌ Birthday API Error:", data.error, data.details)
          setHasError(true)
          toast({
            title: "Database Connection Issue",
            description: data.details || data.error || "Unable to connect to database. Please contact administrator.",
            variant: "destructive",
          })
          setStudents([])
        } else {
          setStudents(Array.isArray(data.students) ? data.students : [])
          setHasError(false)
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setHasError(true)
          toast({
            title: "Unable to load birthdays",
            description:
              error instanceof Error ? error.message : "Unexpected error occurred",
            variant: "destructive",
          })
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadBirthdays()

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [toast])

  useEffect(() => {
    if (!api || students.length <= 1) return

    const autoplay = setInterval(() => {
      if (pauseRef.current || !api) return

      if (api.canScrollNext()) {
        api.scrollNext()
      } else {
        api.scrollTo(0)
      }
    }, 4000)

    return () => {
      clearInterval(autoplay)
    }
  }, [api, students.length])

  useEffect(() => {
    if (api) {
      api.scrollTo(0)
    }
  }, [api, students.length])

  const hasStudents = students.length > 0

  const loadingPlaceholder: BirthdayStudent = {
    id: "loading",
    name: "Loading birthdays...",
    standard: "Please wait",
    image: PLACEHOLDER_IMAGE,
  }

  const carouselItems = hasStudents
    ? students
    : [
      isLoading
        ? loadingPlaceholder
        : {
          id: "placeholder",
          name: hasError ? "Could not load birthdays" : "No birthdays today",
          standard: hasError
            ? "Please try again later"
            : "We will celebrate with you soon!",
          image: PLACEHOLDER_IMAGE,
        },
    ]

  return (
    <section className="relative overflow-hidden py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-[#fff8f8] via-[#ffe7e7] to-white">
      <div className="pointer-events-none absolute inset-0 -z-20 bg-[radial-gradient(circle,rgba(248,113,113,0.18)_1px,transparent_1px)] [background-size:22px_22px]" />
      <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden">
        {balloons.map((balloon) => (
          <span
            key={balloon.id}
            style={{
              left: balloon.left,
              animationDelay: balloon.delay,
              animationDuration: balloon.duration,
              width: `${balloon.size}px`,
              height: `${balloon.size * 1.3}px`,
            }}
            className={`${balloon.color} balloon absolute bottom-[-25%] rounded-full opacity-90 shadow-[0_10px_25px_rgba(248,113,113,0.25)]`}
          />
        ))}
        {confettiPieces.map((piece) => (
          <span
            key={piece.id}
            style={{
              left: piece.left,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
              width: `${piece.size}px`,
              height: `${piece.size * 2}px`,
            }}
            className={`${piece.color} confetti absolute top-[-10%] rounded-sm`}
          />
        ))}
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-red-200 bg-red-100/80 text-red-700 shadow-sm">
            Birthdays & Celebrations
          </span>
          <div className="flex justify-center">
            <div className="ribbon relative inline-flex items-center justify-center px-8 sm:px-12 py-3 sm:py-4 text-white font-serif text-2xl sm:text-3xl md:text-4xl font-bold">
              Birthday Stars of the Week
            </div>
          </div>
        </div>

        <Carousel
          setApi={setApi}
          opts={{ align: "start", loop: true }}
          className="w-full"
          onMouseEnter={() => {
            pauseRef.current = true
          }}
          onMouseLeave={() => {
            pauseRef.current = false
          }}
          onTouchStart={() => {
            pauseRef.current = true
          }}
          onTouchEnd={() => {
            pauseRef.current = false
          }}
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {carouselItems.map((student) => (
              <CarouselItem
                key={student.id}
                className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/4"
              >
                <article className="h-full rounded-[28px] border border-red-100/70 bg-white/95 shadow-[0px_20px_45px_rgba(185,28,28,0.12)] flex flex-col overflow-hidden">
                  <div className="relative w-full aspect-[4/3] bg-gray-100">
                    <Image
                      src={student.image}
                      alt={student.name}
                      fill
                      className="object-fill"
                      priority={student.id === "loading" || student.id === "placeholder"}
                      onError={(event) => {
                        event.currentTarget.src = PLACEHOLDER_IMAGE
                      }}
                    />
                  </div>
                  <div className="px-6 py-6 space-y-2 text-center">
                    <h4 className="font-serif text-lg sm:text-xl font-bold text-[#7a1111]">
                      {student.name}
                    </h4>
                    <p className="text-sm sm:text-base font-medium text-[#5f2530]">
                      {student.standard.charAt(0).toUpperCase() + student.standard.slice(1)}
                    </p>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
      <style jsx>{`
        @keyframes floatBalloon {
          0% {
            transform: translateY(0) rotate(-2deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(-120vh) rotate(4deg);
            opacity: 0;
          }
        }

        .balloon {
          animation-name: floatBalloon;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }

        @keyframes confettiDrop {
          0% {
            transform: translateY(-10vh) rotate(0deg);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            transform: translateY(110vh) rotate(240deg);
            opacity: 0;
          }
        }

        .confetti {
          animation-name: confettiDrop;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }

        .ribbon {
          background: linear-gradient(135deg, #f97373 0%, #ef4444 45%, #c81e3a 100%);
          box-shadow: 0 12px 36px rgba(236, 72, 153, 0.4);
        }

        .ribbon::before,
        .ribbon::after {
          content: "";
          position: absolute;
          bottom: -14px;
          width: 24px;
          height: 20px;
          background: inherit;
          clip-path: polygon(0 0, 100% 0, 0 100%);
          filter: brightness(0.9);
        }

        .ribbon::before {
          left: 0;
          transform: translateX(-60%) skewX(-20deg);
        }

        .ribbon::after {
          right: 0;
          transform: translateX(60%) scaleX(-1) skewX(-20deg);
        }
      `}</style>
    </section>
  )
}

